import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import Dialog from '@pelckmans/elementis/components/dialog';
import GeneralNotice from '@pelckmans/elementis/components/general-notice';
import List from '@pelckmans/elementis/components/list';
import SearchField from '@pelckmans/elementis/components/search-field';
import { useContext, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../../contexts/ModalContext';
import LocalizedDateField, {
  dateFormatter,
} from '../../../../../shared-components/elementis/components/localized-date-field';
import { ScorableItemTableDataGroup } from '../per-student/hooks/useTableData';
import ScorableItemRowGroup from './ScorableItemRowGroup';
import useScorableItemFilters, { ResultType } from './hooks/useScorableItemFilters';

interface Props {
  id: string;
  tableDataGroups: ScorableItemTableDataGroup[];
  onConfirm: (itemIds: string[]) => void;
  selectedItemIds?: string[];
}

export default function ScorableItemFilterModal({ id, tableDataGroups, onConfirm, selectedItemIds }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [blurred, setBlurred] = useState(false);

  const {
    result,
    filterActive,
    scorableItemType,
    listItems,
    titleFilterText,
    hasSelectedItems,
    setScorableItemType,
    setTitleFilterText,
    setTitleFilters,
    getCheckboxState,
    setNodeCheckboxState,
    setScorableItemCheckboxState,
    setDateRange,
    selectAllItems,
    clearAllItems,
    clearFilters,
  } = useScorableItemFilters({
    tableDataGroups,
    selectedItemIds,
  });

  const [dateRangeControl, setDateRangeControl] = useState<DateRange | undefined>();

  useEffect(() => {
    if (!dateRangeControl || (dateRangeControl.from && dateRangeControl.to)) {
      setDateRange(dateRangeControl, scorableItemType);
    }
  }, [setDateRange, dateRangeControl, scorableItemType]);

  function handleTypeCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, type: Exclude<ResultType, 'all'>) {
    if (scorableItemType === 'all') {
      if (!e.target.checked && type === 'free-exercise') setScorableItemType('assignment');
      else if (!e.target.checked && type === 'assignment') setScorableItemType('free-exercise');
    } else if (scorableItemType === 'assignment' || scorableItemType === 'free-exercise') {
      setScorableItemType(e.target.checked ? 'all' : 'none');
    } else {
      setScorableItemType(type);
    }
  }

  function handleClose() {
    closeModal(id);
  }

  function handleConfirmClick() {
    if (!hasSelectedItems) {
      setBlurred(true);
      return;
    }

    const scorableItemIdsOnly = result.filter(id => {
      const [, isAssignmentString] = id.split('-');
      return isAssignmentString === 'true' || isAssignmentString === 'false';
    });

    onConfirm(scorableItemIdsOnly);
    handleClose();
  }

  function renderDateRangeValue(range: DateRange) {
    return t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.VALUE', {
      from: dateFormatter.format(range.from),
      to: range.to ? dateFormatter.format(range.to) : '...',
    });
  }

  const isEmpty = listItems.length === 0;

  return (
    <Dialog
      id={id}
      modal
      className="scorable-item-filter-modal"
      onClose={handleClose}
      size="sm"
      aria-label={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.A11Y_LABEL')}
    >
      <Dialog.Header
        title={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TITLE')}
        onClose={handleClose}
        aria-label={t('ACTIONS.CLOSE')}
        filters={
          <>
            <div className="filter-input input-checkbox-group">
              <Checkbox
                checked={['all', 'free-exercise'].includes(scorableItemType)}
                onChange={e => handleTypeCheckboxChange(e, 'free-exercise')}
              >
                {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TYPE_SELECT.FREE-EXERCISE')}
              </Checkbox>
              <Checkbox
                checked={['all', 'assignment'].includes(scorableItemType)}
                onChange={e => handleTypeCheckboxChange(e, 'assignment')}
              >
                {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TYPE_SELECT.ASSIGNMENT')}
              </Checkbox>
            </div>
            <LocalizedDateField
              mode="range"
              selected={dateRangeControl}
              onSelect={setDateRangeControl}
              aria-label={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.A11Y_LABEL')}
              required
              placeholder={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.PLACEHOLDER')}
              disabled={scorableItemType === 'none' || scorableItemType === 'free-exercise'}
              renderValue={renderDateRangeValue}
              dateMenuAriaLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.DATE_MENU.A11Y_LABEL')}
              size="sm"
              className="filter-input"
            />
          </>
        }
      >
        <SearchField
          value={titleFilterText}
          onClear={() => {
            setTitleFilterText('');
            setTitleFilters('');
          }}
          onSearch={() => setTitleFilters(titleFilterText)}
          onChange={e => setTitleFilterText(e.target.value)}
          placeholder={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.PLACEHOLDER')}
          clearActionLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.CLEAR_A11Y_LABEL')}
          searchActionLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.BUTTON_TEXT')}
        />
      </Dialog.Header>
      <Dialog.Body
        alertGroupProps={
          blurred && !hasSelectedItems
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Alert prominence="warning">
                    {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.NO_SELECTION.ALERT')}
                  </Alert>
                ),
              }
            : undefined
        }
      >
        {isEmpty ? (
          <GeneralNotice
            text={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.EMPTY.TITLE')}
            subText={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.EMPTY.TEXT')}
            action={
              <Button prominence="neutral" size="sm" onClick={() => clearFilters()}>
                {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.EMPTY.ACTION')}
              </Button>
            }
          />
        ) : (
          <List className="scorable-items">
            {listItems?.map(listItem => (
              <ScorableItemRowGroup
                filterActive={filterActive}
                tableDataGroup={listItem}
                key={listItem.id}
                getCheckboxState={getCheckboxState}
                setNodeCheckboxState={setNodeCheckboxState}
                setScorableItemCheckboxState={setScorableItemCheckboxState}
              />
            ))}
          </List>
        )}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={{
          size: 'md',
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="ghost" key="select-all" onClick={selectAllItems}>
                {t('ACTIONS.SELECT_ALL')}
              </Button>
              <Button prominence="ghost" key="clear-all" onClick={clearAllItems} disabled={!hasSelectedItems}>
                {t('ACTIONS.CLEAR_SELECTION')}
              </Button>
            </>
          ),
        }}
        buttonGroupPropsRight={{
          size: 'md',
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('ACTIONS.CANCEL')}
              </Button>
              <Button prominence="success" onClick={handleConfirmClick} key="apply">
                {t('ACTIONS.APPLY')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
